import React, { useState } from 'react';
import { Button, Theme, styled } from '@material-ui/core';
import { Confirm } from '../../../userManagement/components/Confirm';
import { useDiamPlatformAdmin } from './useDiamPlatformAdmin';
import {
  IDENTITY_STATUS,
  IdentityDisplayStatus,
} from '../IdentitiesGrid/types';
import {
  DEACTIVATE_CONFIRM,
  REACTIVATE_CONFIRM,
  RESUME_ACCESS_CONFIRM,
  SUSPEND_ACCESS_CONFIRM,
} from './constants';
import { AccessActions } from './styles';

type AccessAction = 'deactivate' | 'suspend' | 'reactivate' | 'resume';
type AccessActionTone = 'danger' | 'dangerText';

interface AccessActionItem {
  action: AccessAction;
  confirm: typeof DEACTIVATE_CONFIRM;
  label: string;
  nextStatus: IdentityDisplayStatus;
  requiresSuperAdmin?: boolean;
  tone?: AccessActionTone;
  variant: 'text' | 'contained';
  visibleWhen: IdentityDisplayStatus;
}

const ACCESS_ACTIONS: AccessActionItem[] = [
  {
    action: 'reactivate',
    confirm: REACTIVATE_CONFIRM,
    label: 'Activate',
    nextStatus: IDENTITY_STATUS.ACTIVE,
    variant: 'contained',
    visibleWhen: IDENTITY_STATUS.DEACTIVATED,
  },
  {
    action: 'resume',
    confirm: RESUME_ACCESS_CONFIRM,
    label: 'Restore Access',
    nextStatus: IDENTITY_STATUS.ACTIVE,
    variant: 'contained',
    visibleWhen: IDENTITY_STATUS.SUSPENDED,
  },
  {
    action: 'suspend',
    confirm: SUSPEND_ACCESS_CONFIRM,
    label: 'Suspend Access',
    nextStatus: IDENTITY_STATUS.SUSPENDED,
    tone: 'dangerText',
    variant: 'text',
    visibleWhen: IDENTITY_STATUS.ACTIVE,
  },
  {
    action: 'deactivate',
    confirm: DEACTIVATE_CONFIRM,
    label: 'Deactivate',
    nextStatus: IDENTITY_STATUS.DEACTIVATED,
    requiresSuperAdmin: true,
    tone: 'danger',
    variant: 'contained',
    visibleWhen: IDENTITY_STATUS.ACTIVE,
  },
];

const AccessActionButton = styled(Button)(
  ({ theme, tone }: { theme: Theme; tone?: AccessActionTone }) => ({
    fontWeight: 700,
    ...(tone === 'danger' && {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
    }),
    ...(tone === 'dangerText' && {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.error.dark,
      },
    }),
  }),
);

interface IdentityAccessActionsProps {
  status: IdentityDisplayStatus;
  onStatusChange: (status: IdentityDisplayStatus) => void;
}

export function IdentityAccessActions({
  status,
  onStatusChange,
}: IdentityAccessActionsProps) {
  const { isPlatformAdmin: isSuperAdmin } = useDiamPlatformAdmin();
  const [dialog, setDialog] = useState<AccessAction | null>(null);
  const activeAction = ACCESS_ACTIONS.find(item => item.action === dialog);
  const visibleActions = ACCESS_ACTIONS.filter(
    item =>
      item.visibleWhen === status && (!item.requiresSuperAdmin || isSuperAdmin),
  );

  function handleConfirm() {
    if (!activeAction) return;

    onStatusChange(activeAction.nextStatus);
    setDialog(null);
  }

  return (
    <>
      <AccessActions>
        {visibleActions.map(item => (
          <AccessActionButton
            key={item.action}
            color="primary"
            tone={item.tone}
            variant={item.variant}
            onClick={() => setDialog(item.action)}
          >
            {item.label}
          </AccessActionButton>
        ))}
      </AccessActions>
      <Confirm
        isOpen={Boolean(activeAction)}
        title={activeAction?.confirm.title ?? ''}
        content={activeAction?.confirm.content ?? ''}
        confirm={activeAction?.confirm.confirm}
        cancel={activeAction?.confirm.cancel}
        onConfirm={handleConfirm}
        onClose={() => setDialog(null)}
      />
    </>
  );
}
